if (!customElements.get("dynamic-form")) {
  customElements.define(
    "dynamic-form",
    
    /**
     * @requires RMIT.analytics
     * @requires RMIT.UserDevice
     * @requires RMIT.userLocation
     * @requires RMIT.TrafficSource
     * @requires RMIT.countries
     */
    class DynamicForm extends HTMLElement {
      EXPERIAN_ERROR_CODES = ["EXPERIAN_REJECTED_MOBILE", "EXPERIAN_REJECTED_EMAIL", "EXPERIAN_REJECTED_EMAIL_AND_MOBILE"]

      constructor() {
        super();
        this.formFields = {};
        this.DEFAULT_EMAIL_MESSAGE =
          "Please check the email you've provided and try again";
        this.DEFAULT_MOBILE_MESSAGE =
          "Please check the number you've provided and try again";
      }

      createPostcodeField(inputEl, fieldConfig) {
        if (!(fieldConfig.name === "postalCode" || (fieldConfig.validation && fieldConfig.validation.includes("postcode")))) {
          return;
        }

        inputEl.type = "text";
        inputEl.autocomplete = "postal-code";
        inputEl.maxLength = 4;
        // This should prompt for a numeric keypad on mobile devices
        inputEl.setAttribute("inputmode", "numeric");
        inputEl.setAttribute("pattern", "[0-9]*");

        // Filter any non-numeric characters
        inputEl.addEventListener('input', (e) => {
          const digits = e.target.value.replace(/[^0-9]/g, '');
            e.target.value = digits.length > 4 ? digits.slice(0, 4) : digits;
        });
      }

      async connectedCallback() {
        const encodedJson = this.getAttribute("form-json");
        if (!encodedJson) {
          console.warn(
            "No form configuration provided via form-json attribute"
          );
          return;
        }

        await window.RMIT.countries.fetch();

        try {
          const decoded = atob(encodedJson);

          this.formConfig = JSON.parse(decoded);
          this.apiUrl = this.getAttribute("data-api_url");
          this.pageType = this.getAttribute("data-page_type");
          // Future Skills plus some other text | University Pathways
          this.productType = this.getAttribute("data-product_type");
          this.courseId = this.getAttribute("data-course_id");
          this.category = this.getAttribute("data-category");
          this.subcategory = this.getAttribute("data-subcategory");
          this.productHandle = this.getAttribute("data-product_handle");
          this.productId = this.getAttribute("data-product_id");
          // console.log("pageType", this.pageType)
          // console.log("productType", this.productType)
          // console.log("course id", this.courseId)
          // console.log("category", this.category)
          // console.log("subcategory", this.subcategory)
          // console.log("productHandle", this.productHandle)

          // TODO: review if country detection should be a flag rather than a field name in form JSON schema
          // find if countryIsoCode field exists
          this.countryField = this.formConfig?.form?.find(input => input.name === 'countryIsoCode');

          // both the residence select and the mobile dial code need a detected country
          if (!this.countryField && !this.formConfig?.form?.some(input => input.type === 'mobile')) {
            this.initializeForm();
            return;
          }

          // detect country before initialize the form so that dependent fields can be shown/hidden correctly
          window.RMIT.userLocation.getCountry()
            .then((countryCode) => {
              this.detectedCountryCode = countryCode;

              if (this.countryField) {
                // the country code will drive visibility of other fields
                this.countryField.value = countryCode;
              }
            })
            .finally(() => {
              this.initializeForm();
            });
        } catch (err) {
          console.error("Error decoding or parsing form JSON:", err);
          return;
        }
      }

      initializeForm() {
        // Create the main form element
        this.form = document.createElement("form");
        this.form.classList.add("form-container");

        // Create each form field
        if (this.formConfig.form && Array.isArray(this.formConfig.form)) {
          this.formConfig.form.forEach((fieldConfig) => {
            this.createField(fieldConfig);
          });
        }

        // Add submit button
        const submitBtn = document.createElement("button");
        submitBtn.type = "submit";
        submitBtn.classList.add("form-submit");
        // a wrapper div to center the button horizontally
        const submitWrapper = document.createElement("div");
        submitWrapper.classList.add("submit-wrapper");
        // Append the button to button wrapper
        submitWrapper.appendChild(submitBtn);


        const submitLabel = this.getAttribute('data-submit_text') || 'Submit';
        const loaderHtml = `<span>${submitLabel}</span> <svg version="1.1" class="loader-1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" width="40px" height="40px" viewBox="0 0 50 50" style="enable-background:new 0 0 50 50;" xml:space="preserve">
        <path fill="#000" d="M43.935,25.145c0-10.318-8.364-18.683-18.683-18.683c-10.318,0-18.683,8.365-18.683,18.683h4.068c0-8.071,6.543-14.615,14.615-14.615c8.072,0,14.615,6.543,14.615,14.615H43.935z">
          <animateTransform attributeType="xml" attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="0.6s" repeatCount="indefinite"></animateTransform>
        </path>
      </svg>`
        submitBtn.innerHTML = loaderHtml;
        // submitBtn.textContent = "Submit";
        this.form.appendChild(submitWrapper);

        // Setup form submission handler
        this.form.addEventListener("submit", this.handleSubmit.bind(this));

        // Initialize field visibility based on conditions
        this.updateFieldVisibility();

        // Append the form to this custom element
        this.appendChild(this.form);
      }

      normaliseMobile(mobile) {
        const trimmed = mobile ? mobile.trim() : "";
        const prefix = trimmed.startsWith("+") ? "+" : "";
        return prefix + trimmed.replace(/\D/g, "");
      }

      buildDialCodeLabel(country, showFlags) {
        // the Other entry carries no dial code, and adds no prefix on submit, so it reads as its name alone
        if (!country.dial_code) {
          return country.name;
        }

        const flag = showFlags ? country.flag : "";

        // filtered, so a country with no flag and every country on Windows avoid a double space
        return [country.name, flag, `(${country.dial_code})`].filter(Boolean).join(" ");
      }

      createCountrySelectField(selectEl) {
        window.RMIT.countries.list.forEach(country => {
          const opt = document.createElement("option");
          opt.value = country.code;
          opt.textContent = country.name;
          selectEl.appendChild(opt);
        });
        
        // Wait for the DOM to render before setting the default value.
        setTimeout(() => {
          const detected = this.countryField?.value;
          selectEl.value = !detected || detected === 'XX' ? '' : detected;
        });
      }

      createField(fieldConfig) {
        const container = document.createElement("div");
        container.classList.add("form-group");

        // Label (if any)
        if (fieldConfig.label && fieldConfig.type !== "checkbox") {
          const labelEl = document.createElement("label");
          labelEl.classList.add("form-label");
          labelEl.textContent = fieldConfig.label;

          // Add red asterisk for required fields
          if (
            fieldConfig.validation &&
            (fieldConfig.validation.includes("required") ||
              fieldConfig.validation.includes("requiredMobile"))
          ) {
            const requiredAsterisk = document.createElement("span");
            requiredAsterisk.classList.add("rmit-red");
            requiredAsterisk.textContent = " *";
            labelEl.appendChild(requiredAsterisk);
          }

          container.appendChild(labelEl);
        }

        let inputEl;

        switch (fieldConfig.type) {
          case "input":
            inputEl = document.createElement("input");
            inputEl.classList.add("form-input");
            inputEl.name = fieldConfig.name;
            if (fieldConfig.placeholder) {
              inputEl.placeholder = fieldConfig.placeholder;
            }
            // Setup postcode-specific functionality
            this.createPostcodeField(inputEl, fieldConfig);
            break;

          case "select":
            inputEl = document.createElement("select");
            inputEl.classList.add("form-select");
            inputEl.name = fieldConfig.name;
            if (fieldConfig.placeholder) {
              const placeholderOption = document.createElement("option");
              placeholderOption.value = "";
              placeholderOption.textContent = fieldConfig.placeholder;
              inputEl.appendChild(placeholderOption);
            }
            if (fieldConfig.options) {
               if (fieldConfig.options === "countries") {
                this.createCountrySelectField(inputEl);
              }
              else if (!Array.isArray(fieldConfig.options)) {
                // If it's an object: {value: label}
                Object.keys(fieldConfig.options).forEach((optVal) => {
                  const opt = document.createElement("option");
                  opt.value = optVal;
                  opt.textContent = fieldConfig.options[optVal];
                  inputEl.appendChild(opt);
                });
              } else {
                // If it's an array of {label, value}
                fieldConfig.options.forEach((optData) => {
                  const opt = document.createElement("option");
                  opt.value = optData.value;
                  opt.textContent = optData.label;
                  inputEl.appendChild(opt);
                });
              }
            }
            break;

          case "checkbox":
            const checkboxWrapper = document.createElement("div");
            checkboxWrapper.classList.add("checkbox-wrapper");

            // Create label first
            const checkboxLabel = document.createElement("label");
            checkboxLabel.htmlFor = fieldConfig.name;
            checkboxLabel.classList.add("checkbox-label");

            // Process label text with links
            const labelText = fieldConfig.label;

            // Check if there's a link pattern in the format [url]text[/end]
            const linkPattern = /\[(https?:\/\/[^\]]+)\](.*?)\[\/end\]/g;

            if (linkPattern.test(labelText)) {
              // If link pattern exists, create the formatted label with links
              const processedLabel = labelText.replace(
                linkPattern,
                function (match, url, text) {
                  return `<a href="${url}" target="_blank" rel="noopener noreferrer">${text}</a>`;
                }
              );

              // Use innerHTML to render the links
              checkboxLabel.innerHTML = processedLabel;
            } else {
              // No links, just set text content
              checkboxLabel.textContent = labelText;
            }

            // Add required asterisk if needed
            if (
              fieldConfig.validation &&
              fieldConfig.validation.includes("required")
            ) {
              const requiredAsterisk = document.createElement("span");
              requiredAsterisk.classList.add("rmit-red");
              requiredAsterisk.textContent = " *";
              checkboxLabel.appendChild(requiredAsterisk);
            }

            // Create input inside label
            const checkboxInput = document.createElement("input");
            checkboxInput.type = "checkbox";
            checkboxInput.id = fieldConfig.name;
            checkboxInput.name = fieldConfig.name;
            checkboxInput.checked = fieldConfig.value === true;

            // Create custom checkmark span
            const checkmark = document.createElement("span");
            checkmark.classList.add("checkmark-thick");

            // Add input and checkmark to label
            checkboxLabel.appendChild(checkboxInput);
            checkboxLabel.appendChild(checkmark);

            // Add the label (containing everything) to the wrapper
            checkboxWrapper.appendChild(checkboxLabel);

            // Add wrapper to container
            container.appendChild(checkboxWrapper);

            // Store the input reference
            inputEl = checkboxInput;

            // Set flag to prevent double-appending inputs break out of label container
            fieldConfig._inputAppended = true;
            break;

          case "radio":
            const radioContainer = document.createElement("div");
            radioContainer.classList.add("form-radio-group");

            if (Array.isArray(fieldConfig.options)) {
              fieldConfig.options.forEach((opt) => {
                // Create form__radio-group container for each option
                const radioGroup = document.createElement("div");
                radioGroup.classList.add("form__radio-group");

                // Create radio input
                const radioId = `${fieldConfig.name}_${opt.value}`;
                const radioInput = document.createElement("input");
                radioInput.type = "radio";
                radioInput.id = radioId;
                radioInput.name = fieldConfig.name;
                radioInput.value = opt.value;
                radioInput.classList.add("form__radio-input");

                // Check if this option should be pre-selected
                const valIsBoolean = typeof fieldConfig.value === "boolean";
                if (valIsBoolean) {
                  if (fieldConfig.value === opt.value) {
                    radioInput.checked = true;
                  }
                } else {
                  // It's a string or something else
                  if (String(fieldConfig.value) === String(opt.value)) {
                    radioInput.checked = true;
                  }
                }

                // Create label with custom radio button
                const radioLabel = document.createElement("label");
                radioLabel.htmlFor = radioId;
                radioLabel.classList.add("form__radio-label");

                // Create custom radio button span
                const radioButton = document.createElement("span");
                radioButton.classList.add("form__radio-button");

                // Create label text span
                const labelText = document.createElement("span");
                labelText.classList.add("form__radio-label-text");
                labelText.textContent = opt.label;

                // Assemble the components
                radioLabel.appendChild(radioButton);
                radioLabel.appendChild(labelText);

                // Add input and label to the group
                radioGroup.appendChild(radioInput);
                radioGroup.appendChild(radioLabel);

                // Add the group to the container
                radioContainer.appendChild(radioGroup);

                // Add event listener for visibility updates
                radioInput.addEventListener(
                  "change",
                  this.updateFieldVisibility.bind(this)
                );
              });
            }

            container.appendChild(radioContainer);
            inputEl = radioContainer; // We'll store the container as the "element"
            break;

          case "textarea":
            inputEl = document.createElement("textarea");
            inputEl.classList.add("form-textarea");
            inputEl.name = fieldConfig.name;
            if (fieldConfig.placeholder) {
              inputEl.placeholder = fieldConfig.placeholder;
            }
            break;

          case "mobile":
            const mobileWrapper = document.createElement("div");
            mobileWrapper.classList.add("mobile-wrapper");

            // appended here, not in countries.json, which also feeds the residence select and the country name lookup on submit
            const dialCodeOptions = [
              ...window.RMIT.countries.list,
              {
                name: "Other",
                code: "OTHER",
                dial_code: "",
                flag: ""
              }
            ];
            const detectedCountry = dialCodeOptions.find((country) => country.code === this.detectedCountryCode);

            const preselected =
              detectedCountry ||
              dialCodeOptions.find((country) => country.code === "AU") ||
              dialCodeOptions[0];

            const countrySelect = document.createElement("select");
            countrySelect.classList.add("mobile-country-select");
            countrySelect.setAttribute("aria-label", "Country dialling code");

            // Windows omits flag glyphs from its emoji font, so Chromium there renders the ISO letters where the flag should be
            const showFlags = window.RMIT.userDevice.getOS() !== "Windows";

            const appendCountryOption = (country, parent) => {
              const opt = document.createElement("option");
              opt.value = country.code;
              opt.textContent = this.buildDialCodeLabel(country, showFlags);
              parent.appendChild(opt);
            };

            // only rendered when detection succeeded, so the label never claims the Australia fallback is the visitor's location
            if (detectedCountry) {
              const currentLocationGroup = document.createElement("optgroup");
              currentLocationGroup.label = "Current Location";
              appendCountryOption(detectedCountry, currentLocationGroup);
              countrySelect.appendChild(currentLocationGroup);
            }

            const allCountriesGroup = document.createElement("optgroup");
            allCountriesGroup.label = "All Countries";
            dialCodeOptions.forEach((country) => {
              appendCountryOption(country, allCountriesGroup);
            });
            countrySelect.appendChild(allCountriesGroup);

            // the detected country appears in both groups with the same value, and this selects the Current Location entry because it comes first
            countrySelect.value = preselected.code;

            // The native select cannot truncate the country name separately from the flag and dial code.
            const countryControl = document.createElement("div");
            countryControl.className = "mobile-country-control";
            countryControl.innerHTML = `
              <span class="mobile-country-display" aria-hidden="true">
                <span class="mobile-country-name"></span>
                <span class="mobile-country-details"></span>
              </span>
            `;
            countryControl.appendChild(countrySelect);
            mobileWrapper.appendChild(countryControl);

            const countryName = countryControl.querySelector(".mobile-country-name");
            const countryDetails = countryControl.querySelector(".mobile-country-details");

            // Phone input
            // todo: missing reformatting logic. can reuse the stuff from the vue repo or the shop checkout extensions repo but it will require some effort to graft it in.
            const phoneInput = document.createElement("input");
            phoneInput.type = "tel";
            phoneInput.classList.add("form-input", "form-noborder");
            phoneInput.name = fieldConfig.name;
            if (fieldConfig.placeholder) {
              phoneInput.placeholder = fieldConfig.placeholder;
            }
            mobileWrapper.appendChild(phoneInput);

            // Insert into container
            container.appendChild(mobileWrapper);

            // Error message container
            const errorEl = document.createElement("div");
            errorEl.classList.add("form-error");
            container.appendChild(errorEl);

            // Store field data
            if (!this.formFields[fieldConfig.name]) { this.formFields[fieldConfig.name] = []; }
            this.formFields[fieldConfig.name].push({
              config: fieldConfig,
              container,
              element: phoneInput, // the main "element" for validation references
              errorEl,
              phoneInput: phoneInput,
              selectedCountry: preselected,
            });

            const updateSelectedCountry = () => {
              const selectedCountry = dialCodeOptions.find(
                (country) => country.code === countrySelect.value
              ) || preselected;

              for (const entry of this.formFields[fieldConfig.name]) {
                entry.selectedCountry = selectedCountry;
              }

              const label = this.buildDialCodeLabel(selectedCountry, showFlags);
              countryName.textContent = selectedCountry.name;
              countryDetails.textContent = label.slice(selectedCountry.name.length).trim();
            };

            updateSelectedCountry();
            countrySelect.addEventListener("input", updateSelectedCountry);
            countrySelect.addEventListener("change", updateSelectedCountry);

            // Add to form
            this.form.appendChild(container);

            // Listen for changes to recalc condition visibility
            phoneInput.addEventListener(
              "change",
              this.updateFieldVisibility.bind(this)
            );

            return; // Skip the rest of the function since we've handled everything

          default:
            // Fallback to a normal input
            inputEl = document.createElement("input");
            inputEl.classList.add("form-input");
            inputEl.name = fieldConfig.name;
            if (fieldConfig?.type) {
              inputEl.type = fieldConfig.type;
            }
            break;
        }

        // Pre-set a default value, if provided (and not a radio or mobile)
        if (
          fieldConfig.type !== "radio" &&
          fieldConfig.type !== "mobile" &&
          fieldConfig.value !== undefined
        ) {
          inputEl.value = fieldConfig.value;
        }

        // Error message container
        const errorEl = document.createElement("div");
        errorEl.classList.add("form-error");

        // Append elements (if not already appended)
        if (
          fieldConfig.type !== "radio" &&
          fieldConfig.type !== "mobile" &&
          fieldConfig.type !== "checkbox"
        ) {
          container.appendChild(inputEl);
        }
        container.appendChild(errorEl);
        this.form.appendChild(container);


        if (!this.formFields[fieldConfig.name]) {
          this.formFields[fieldConfig.name] = [];
        }
        // Store field data
        this.formFields[fieldConfig.name].push({
          config: fieldConfig,
          container: container,
          element: inputEl,
          errorEl: errorEl
        });

        // Listen for changes to recalc condition visibility
        if (fieldConfig.type === "radio") {
          const radios = container.querySelectorAll('input[type="radio"]');
          radios.forEach((radio) => {
            radio.addEventListener(
              "change",
              this.updateFieldVisibility.bind(this)
            );
          });
        } else if (fieldConfig.type !== "mobile") {
          inputEl.addEventListener(
            "change",
            this.updateFieldVisibility.bind(this)
          );
        }
      }

      handleSubmit(e) {
        e.preventDefault();
        this.clearServerSideErrors(); // Clear leftover server-side errors

        // 1. Client-side validation
        const errors = this.validateForm();

        Object.keys(this.formFields).forEach((fieldName) => {
          const arr = Array.isArray(this.formFields[fieldName])
            ? this.formFields[fieldName]
            : [this.formFields[fieldName]];
          arr.forEach((entry) => {
            entry.errorEl.textContent = "";
            entry.errorEl.classList.remove("active");
          });
        });

        // Show new errors
        let hasError = false;
        Object.keys(errors).forEach((fieldName) => {
          const arr = Array.isArray(this.formFields[fieldName])
            ? this.formFields[fieldName]
            : [this.formFields[fieldName]];

          const visibleOrFirst = 
            arr.find((entry) => !entry.container.classList.contains("hidden")) ||
            arr[0];
          visibleOrFirst.errorEl.textContent = errors[fieldName];
          visibleOrFirst.errorEl.classList.add("active");
          hasError = true;
        });

        if (!hasError) {
          // Build final payload
          const finalPayload = {};
          const values = {};
          const currentValues = this.getCurrentValues(); // now array-aware
          
          // Only include fields whose condition is satisfied
          Object.keys(this.formFields).forEach((fieldName) => {
            // We might have multiple entries; if any one is visible, it means we want to capture it
            const arr = Array.isArray(this.formFields[fieldName])
              ? this.formFields[fieldName]
              : [this.formFields[fieldName]];

            // Check if at least one of them passes the 'if' check
            let included = false;
            for (const entry of arr) {
              if (
                !entry.config.if ||
                this.evaluateCondition(entry.config.if, currentValues)
              ) {
                // Visible => we keep the name
                included = true;
                break;
              }
            }

            if (included) {
              values[fieldName] = currentValues[fieldName];
            }
          });

          // Rename fields to map to expected payload keys
          Object.keys(values).forEach((k) => {
            // todo: let the schema override the name but there are too many edge cases to solve that right now
            // because the schema port can't cope with reused input names we had to prefix them; now strip that
            let key = k;
            if(k.startsWith("b2b_")) {
              key = k.replace("b2b_", "")
            }
            // rename optInAus or optInNonAus to optIn
            if (key === "optInAus" || key === "optInNonAus") {
              key = "optIn";
            }
            finalPayload[key] = values[k];
          });

          // Pass the country field as AU even though the user can't see the field.
          if (this.countryField?.value === "AU") {
            finalPayload.country = "AU";
          }

          finalPayload.country = window.RMIT.countries.list.find((country) => country.code === finalPayload.country)?.name ?? finalPayload.country

          // Add additional data from form config
          const additionalData = this.getAdditionalData(values);

          Object.assign(finalPayload, additionalData);

          // todo: fix future skills !b2b submission payload

          const expectedFSNonB2BFields = [
            "isB2B", // boolean
            "firstName",
            "lastName",
            "email",
            "mobile",
            "state",
            "country",
            "startStudying",
            "leadSourceDetail", //
            "type",
            "leadAttribution",
            "portfolio", // "Future Skills"
            "interestedCourseId", // "ABC123"
            "postalCode",
            "optIn",
            "privacyAcknowledgement",
            "clid",
            "type"
          ]

          const expectedFSB2BFields = [
            "leadAttribution",
            "leadSourceDetail",
            "portfolio",
            "interestedCourseId",
            "firstName",
            "lastName",
            "email",
            "mobile",
            "state",
            "country",
            "startStudying",
            "isB2B",
            "company",
            "companySize",
            "numberOfLearners",
            "position",
            "postalCode",
            "optIn",
            "privacyAcknowledgement",
            "clid",
            "type"
          ]

          const expectedBundleNonB2BFields = [
            "leadAttribution",
            "leadSourceDetail",
            "portfolio",
            "programCode",
            "firstName",
            "lastName",
            "email",
            "mobile",
            "state",
            "country",
            "startStudying",
            "isB2B",
            "postalCode",
            "optIn",
            "privacyAcknowledgement",
            "clid",
            "type"
          ]

          const expectedBundleB2BFields = [
            "leadAttribution",
            "leadSourceDetail",
            "portfolio",
            "programCode",
            "firstName",
            "lastName",
            "email",
            "mobile",
            "state",
            "country",
            "startStudying",
            "isB2B",
            "company",
            "companySize",
            "numberOfLearners",
            "position",
            "postalCode",
            "optIn",
            "privacyAcknowledgement",
            "clid",
            "type"
          ]

          const expectedUPNonB2BFields = [
            "leadAttribution",
            "leadSourceDetail",
            "portfolio",
            "interestedCourseId",
            "firstName",
            "lastName",
            "email",
            "mobile",
            "state",
            "country",
            "startStudying",
            "isB2B",
            "postalCode",
            "optIn",
            "privacyAcknowledgement",
            "clid",
            "type"
          ]

          const expectedGeneralNonCoursePageFields = [
            "leadAttribution",
            "leadSourceDetail",
            "portfolio",
            "firstName",
            "lastName",
            "email",
            "mobile",
            "state",
            "country",
            "postalCode",
            "startStudying",
            "optIn",
            "privacyAcknowledgement",
            "category",
            "subcategory",
            "clid",
            "type"
          ]

          const objKeys = Object.keys(finalPayload);
          let fieldSet = [...expectedGeneralNonCoursePageFields];

          if(this.pageType === 'bundle') {
            fieldSet = finalPayload.isB2B ? [...expectedBundleB2BFields] : [...expectedBundleNonB2BFields];
          } else if (this.pageType === 'course' ) {
            fieldSet = finalPayload.isB2B ? [...expectedFSB2BFields] : [...expectedFSNonB2BFields];
          }

          let result = this.pageType + (finalPayload.isB2B ? "B2B": "NotB2B") + "\n";
          objKeys.forEach(key => {
            if(!fieldSet.includes(key)) {
              result += `form key check: unexpected key ${key}\n`
            }
          })
          fieldSet.forEach(key => {
            if(!objKeys.includes(key)) {
              result += `form key check: missing key ${key}\n`
            }
          })

          // console.log(result)

          // Submit the form
          this.submitForm(finalPayload);
        }
        else {
          this.fireFormStatusAnalytics('Brochure failure - front end');
        }
      }

      validateForm() {
        const errors = {};
        const values = this.getCurrentValues();
        let firstField = null;
        Object.keys(this.formFields).forEach((fieldName) => {
          const entries = this.formFields[fieldName];

          for (let i = 0; i < entries.length; i++) {
            const entry = entries[i];
            const fieldConfig = entry.config;

            // skip if hidden
            if (fieldConfig.if && !this.evaluateCondition(fieldConfig.if, values)) {
              continue;
            }

            if (fieldConfig.validation) {
              const rules = fieldConfig.validation.split("|");
              for (let j = 0; j < rules.length; j++) {
                const rule = rules[j];
                // the mobile value carries the dial code, which must not count toward the digit minimum
                const ruleValue = rule === "requiredMobile" && fieldConfig.type === "mobile"
                  ? entry.phoneInput?.value.trim() ?? ""
                  : values[fieldName];

                let errorMessage = this.checkRule(rule, ruleValue, fieldConfig);
                if (errorMessage) {
                  if(firstField === null) {
                    // console.log("focusing on field", fieldName);
                    firstField = entry.element || document.querySelector(`input[name="${fieldName}"]`);
                    firstField.focus();
                    firstField.scrollIntoView({
                      behavior: "smooth",
                      block: "center",
                      inline: "center"
                    });
                  }
                  errors[fieldName] = errorMessage;
                  break; // stop checking more rules
                }
              }              
              if (errors[fieldName]) break; // no need to check more entries
            }
            
          }
        });

        return errors;
      }

      checkRule(rule, fieldVal, fieldConfig) {
        // parse the rule, e.g. "required", "length:2,20", etc.
        const parts = rule.split(":");
        const ruleName = parts[0];

        switch (ruleName) {
          case "required":
          case "required:trim":
            if (!fieldVal || (typeof fieldVal === "string" && !fieldVal.trim())) {
              return fieldConfig["validation-messages"]?.required || "This field is required";
            }
            break;

          case "email2":
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (fieldVal && !emailRegex.test(fieldVal.trim())) {
              return fieldConfig["validation-messages"]?.email2 || "E-mail must be valid";
            }
            break;

          case "length":
            // Example: length:2,20 => check min=2, max=20
            const range = parts[1].split(",");
            const min = parseInt(range[0], 10);
            const max = parseInt(range[1], 10);
            const valLen = fieldVal ? fieldVal.length : 0;
            if (valLen < min || valLen > max) {
              return fieldConfig["validation-messages"]?.length || 
                `Must be between ${min} and ${max} characters`;
            }
            break;

          case "name":
            // Very naive "name" check
            const nameRegex = /^[A-Za-zÀ-ÖØ-öø-ÿ' -]+$/;
            if (fieldVal && !nameRegex.test(fieldVal.trim())) {
              return fieldConfig["validation-messages"]?.name || "Please enter a valid name";
            }
            break;

          case "required-checkbox":
            if (!fieldVal) {
              return fieldConfig["validation-messages"]?.required || "You must check this box to proceed";
            }
            break;

          case "requiredMobile":
            const mobileVal = fieldVal ? fieldVal.replace(/[\s().-]/g, "") : "";

            if (!/^\+?\d{6,15}$/.test(mobileVal)) {
              return fieldConfig["validation-messages"]?.requiredMobile || "A valid phone number is required";
            }
            break;

          case "postcode":
            const postcodeRegex = /^\d{4}$/;
            if (fieldVal && !postcodeRegex.test(fieldVal.trim())) {
              return fieldConfig["validation-messages"]?.postcode || "Postcode must be 4 digits";
            }
            break;
        }
        
        return ""; // No error
      }

      getCurrentValues() {
        const vals = {};
      
        Object.keys(this.formFields).forEach((fieldName) => {
          const entries = this.formFields[fieldName];
      
          let chosenEntry = entries.find(
            (entry) => !entry.container.classList.contains("hidden")
          );
          if (!chosenEntry) {
            chosenEntry = entries[0];
          }
      
          if (!chosenEntry) {
            vals[fieldName] = "";
            return;
          }
      
          const fieldConfig = chosenEntry.config;
          const inputEl = chosenEntry.element;
      
          if (fieldConfig.type === "radio") {
            const checkedRadio = this.form.querySelector(
              'input[name="' + fieldName + '"]:checked'
            );
            if (checkedRadio) {
              if (checkedRadio.value === "true") {
                vals[fieldName] = true;
              } else if (checkedRadio.value === "false") {
                vals[fieldName] = false;
              } else {
                vals[fieldName] = checkedRadio.value;
              }
            } else {
              vals[fieldName] = null;
            }
          }
          else if (fieldConfig.type === "mobile") {
            const phoneInput = chosenEntry.phoneInput;
            const selectedCountry = chosenEntry.selectedCountry || { dial_code: "" };
            const rawPhone = this.normaliseMobile(phoneInput ? phoneInput.value : "");
            vals[fieldName] = (selectedCountry.dial_code || "") + rawPhone.replace(/^0+/, "");

          }
          else if (fieldConfig.type === "checkbox") {
            vals[fieldName] = inputEl.checked;
          }
          else {
            vals[fieldName] = inputEl.value;
          }
        });
      
        return vals;
      }

      evaluateCondition(condition, values) {
        condition = condition.replace(/!([a-zA-Z0-9])/g, "! $1");
        const tokens = condition.split(" ");
        const transformed = tokens
          .map((token) => {
            token = token.trim();
            if (!token) return token;

            // If token is the name of a known field, reference its value
            if (Object.prototype.hasOwnProperty.call(values, token)) {
              return "values['" + token + "']";
            }

            // Recognized operators
            if (
              [
                "===",
                "==",
                "!==",
                "!=",
                "&&",
                "||",
                "!",
                "(",
                ")",
                "true",
                "false",
              ].indexOf(token) > -1
            ) {
              return token;
            }

            // If token starts with "!", interpret as "field is empty or null"
            if (token.startsWith("!")) {
              const possibleField = token.slice(1);
              if (Object.prototype.hasOwnProperty.call(values, possibleField)) {
                return (
                  "(values['" +
                  possibleField +
                  "'] == null || values['" +
                  possibleField +
                  "'] === '')"
                );
              }
            }

            // Otherwise treat as a string literal
            return "'" + token + "'";
          })
          .join(" ");

        try {
          const fn = new Function("values", "return " + transformed + ";");
          return fn(values);
        } catch (e) {
          console.error("Error evaluating condition:", condition, e);
          return false;
        }
      }

      updateFieldVisibility() {
        const currentValues = this.getCurrentValues();
        
        Object.keys(this.formFields).forEach((fieldName) => {
          const entries = this.formFields[fieldName];
          entries.forEach((entry) => {
            const fieldConfig = entry.config;
            if (fieldConfig.if) {
              const shouldShow = this.evaluateCondition(fieldConfig.if, currentValues);
              if (shouldShow) {
                entry.container.classList.remove("hidden");
              } else {
                entry.container.classList.add("hidden");
              }
            } else {
              entry.container.classList.remove("hidden");
            }
          });
        });
      }

      resolveEndpoint(values) {
        if (typeof this.formConfig.endpoint === "string") {
          return this.formConfig.endpoint;
        } else if (
          this.formConfig.endpoint &&
          this.formConfig.endpoint.selector
        ) {
          const selector = this.formConfig.endpoint.selector;
          const selectedValue = values[selector];
          return this.formConfig.endpoint.options[selectedValue] || "";
        }
        return "";
      }

      getAdditionalData(values) {
        let additionalData = {};

        if (this.formConfig.additionalData) {
          Object.keys(this.formConfig.additionalData).forEach((selector) => {
            let selectorValue = values[selector];
            if (
              selectorValue &&
              this.formConfig.additionalData[selector][selectorValue]
            ) {
              Object.assign(
                additionalData,
                this.formConfig.additionalData[selector][selectorValue]
              );
            }
          });
        }

        // Support for about field determining B2B type
        if (values.about === 'org' && !additionalData.type) {
          additionalData.type = 'B2B';
        }

        // Add lead source detail if present
        if (this.formConfig.leadSourceDetail) {
          additionalData.leadSourceDetail = this.formConfig.leadSourceDetail;
        }

        // Add form type if present in config and not already set
        if (this.formConfig.type && !additionalData.type) {
          additionalData.type = this.formConfig.type;
        }

        // todo: portfolio: Generic | Future Skills | University Pathways
        // this seems to be created with some random extra bits trailing in shopify
        const productType = this.productType?.toLocaleLowerCase() ?? "";
        if(productType.includes("future skills")) {
          additionalData.portfolio = "Future Skills";
        } else if (productType.includes("university pathways")) {
          additionalData.portfolio = "University Pathways";
          } else if (productType.includes("non-award short course")) {
          additionalData.portfolio = "Non-Award Short Course";
        } else {
          additionalData.portfolio = "Generic";
        }

        if (window.RMIT.product.sfRelationshipAlignment) {
          additionalData.sfRelationshipAlignment = window.RMIT.product.sfRelationshipAlignment;
        }

        // work out course id or program code
        if(this.courseId) {
          if (this.pageType === 'bundle') {
            additionalData.programCode = this.courseId
          } else {
            additionalData.interestedCourseId = this.courseId
          }
        }

        // Category Pages
        if(this.category) {
          additionalData.category = this.category
        }

        if(this.subcategory) {
          additionalData.subcategory = this.subcategory
        }

        // Get leadAttribution from cookies
        const sourceMedium = window.RMIT.trafficSource.get();
        // console.log('sourceMedium', sourceMedium);
        if (sourceMedium) {
          additionalData.leadAttribution = sourceMedium;
        } else {
          additionalData.leadAttribution = "utmcsr=(direct)|utmcmd=(none)|utmccn=(not set)";
        }

        // Add OS information
        additionalData.os = window.RMIT.userDevice.getOS();

        // Add device information
        additionalData.device = window.RMIT.userDevice.getType();

        // clid set by GTM; read from localStorage
        const clid = getClickIdForLead();
        if(clid) {
          additionalData.clid = clid;
        }

        if (this.countryField?.value) {
          additionalData.submissionOrigin = this.countryField.value === 'AU' ? 'AU' : 'INT';
        }

        // lifted Vue code
        function getClickIdForLead() {
          const clid = JSON.parse(localStorage.getItem('clid'));
          const hasValidExpiryDate = !!clid?.expiryDate;
          const hasExpired = new Date().getTime() >= +clid?.expiryDate;
          if (clid !== null && hasValidExpiryDate && !hasExpired) {
            return clid?.value?.slice?.(0, 255);
          }
        }

        return additionalData;
      }

      submitForm(finalPayload) {
        const submitBtn = this.form.querySelector(".form-submit");
        submitBtn.classList.add("is-loading");
        // Resolve the endpoint
        const endpoint = this.resolveEndpoint(finalPayload);

        if (!endpoint) {
          const generalErrorContainer = this.getGeneralErrorContainer();
          generalErrorContainer.textContent =
            "Could not determine submission endpoint. Please try again.";
          generalErrorContainer.classList.add("active");
          return;
        }

        // Disable submit button during submission
        submitBtn.disabled = true;
        submitBtn.textContent = "Submitting...";

        // For debugging: remove this return in production
        // const currentUrl = encodeURIComponent(window.location.pathname);
        // const currentType = this.pageType; 
        // this.showSuccessMessage(currentUrl, currentType); 
        // // redirect to thank you page
        // return console.log(`POST: ${this.apiUrl}${endpoint}`, finalPayload);
        // End of debug


        fetch(`${this.apiUrl}${endpoint}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(finalPayload),
        })
          .then((response) => {
            if (response.ok) {
              // Success case
              this.fireFormStatusAnalytics('Brochure Success');
              this.fireFormSubmitAnalytics(finalPayload);
              const currentUrl = encodeURIComponent(window.location.pathname);
              const currentType = this.pageType; 
              this.showSuccessMessage(currentUrl, currentType);
              return { success: true };
            } else {
              // Error case - parse the error response
              return response.json().then((errorData) => {
                throw errorData;
              });
            }
          })
          .catch((error) => {
            console.error("Form submission error:", error);
            this.handleServerErrors(error);
          })
          .finally(() => {
            // Re-enable submit button
            submitBtn.disabled = false;
            submitBtn.textContent = "Submit";
            submitBtn.classList.remove("is-loading");
          });
      }

      showSuccessMessage(currentUrl, currentType) {
        if(this.productHandle){
          window.location.href = `/pages/thank-you?ref=${this.productId}`;
        }else{
          window.location.href = `/pages/success?&originalUrl=${currentUrl}&pagetype=${currentType}`;
      //   this.form.style.display = "none";
      //   const checkMarkSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none">
      //   <g clip-path="url(#clip0_3079_41838)">
      //     <path fill-rule="evenodd" clip-rule="evenodd" d="M40 20C40 31.0457 31.0457 40 20 40C8.9543 40 0 31.0457 0 20C0 8.9543 8.9543 0 20 0C31.0457 0 40 8.9543 40 20ZM18.2613 25.3241L28.1483 13.8941L26.8517 12.7726L17.0189 24.14L12.2728 19.3939L11.0606 20.6061L15.8239 25.3694L15.8239 25.3694L15.8285 25.374C15.9879 25.5309 16.1765 25.6523 16.3808 25.7341C16.585 25.8158 16.8029 25.8571 17.0215 25.8571C17.2402 25.8571 17.458 25.8158 17.6623 25.7341C17.8666 25.6523 18.0552 25.5309 18.2145 25.374C18.2308 25.358 18.2464 25.3413 18.2613 25.3241Z" fill="#4FA82E"/>
      //   </g>
      //   <defs>
      //     <clipPath id="clip0_3079_41838">
      //       <rect width="40" height="40" fill="white"/>
      //     </clipPath>
      //   </defs>
      // </svg>`;
      //   const successMessage = document.createElement("div");
      //   successMessage.classList.add("form-success-message");
      //   successMessage.innerHTML =
      //     `${checkMarkSvg}
      //     <h3>Thank you!</h3><p>Thank you for reaching out! Your message has been received, you can call us and one of our team members will get back to you within 1-2 business days.</p>
      //     <p>​If your enquiry is urgent, please call RMIT UP on <a href="tel:+61396575800">+61 3 9657 5800</a> (Monday to Friday 9am to 5pm AEDT).​</p>
      //     `;

      //   this.appendChild(successMessage);
        }
      }

      getGeneralErrorContainer() {
        let container = this.form.querySelector(".form-general-error");
        if (!container) {
          container = document.createElement("div");
          container.classList.add("form-general-error");
          this.form.insertBefore(container, this.form.firstChild);
        }
        return container;
      }

      clearServerSideErrors() {
        Object.keys(this.formFields).forEach((fieldName) => {
          const arr = Array.isArray(this.formFields[fieldName])
            ? this.formFields[fieldName]
            : [this.formFields[fieldName]];
          arr.forEach((entry) => {
            entry.errorEl.textContent = "";
            entry.errorEl.classList.remove("active");
          });
        });

        const generalErrorContainer = this.form.querySelector(".form-general-error");
        if (generalErrorContainer) {
          generalErrorContainer.textContent = "";
          generalErrorContainer.classList.remove("active");
        }
      }

      setServerSideError(fieldName, errorMessage) {
        if (this.formFields[fieldName]) {
          const errorEl = this.formFields[fieldName].errorEl;
          errorEl.textContent = errorMessage;
          errorEl.classList.add("active");
        }
      }

      handleServerErrors(errorResponse) {
        this.clearServerSideErrors();

        // Error code to field name mapping
        const errorCodeMapping = {

          EXPERIAN_REJECTED_MOBILE: "mobile",
          MOBILE_ERROR: "mobile",
          EMAIL_ERROR: "email",
          EXPERIAN_REJECTED_EMAIL: "email",
          EXPERIAN_REJECTED_EMAIL_AND_MOBILE: ["email", "mobile"],
          EXPERIAN_GENERIC_ERROR: "message",
          REQUIRED_FIELD_MISSING: "message",
          FIELD_INVALID: "message",
          FIRST_NAME_ERROR: "firstName",
          LAST_NAME_ERROR: "lastName",
          STATE_ERROR: "state",
          POSITION_ERROR: "position",
          COMPANY_ERROR: "company",
          COMPANY_SIZE_ERROR: "companySize",
          MESSAGE_ERROR: "message",
          START_STUDYING_ERROR: "startStudying",
          CITIZEN_RESIDENT_ERROR: "citizenResident",
          NUMBER_OF_LEARNERS_ERROR: "numberOfLearners",
          ABOUT_ERROR: "about",
          STUDY_TYPE_ERROR: "studyType",
          ENQUIRY_TYPE_ERROR: "enquiryType",
          IS_B2B_ERROR: "isB2B",
          POSTCODE_ERROR: "postalCode",
        };

        
        // Get general error container for non-field errors
        const generalErrorContainer = this.getGeneralErrorContainer();
        generalErrorContainer.textContent = "";
        generalErrorContainer.classList.remove("active");
        
        // Process the error response
        if (typeof errorResponse === "string") {
          generalErrorContainer.textContent = errorResponse;
          generalErrorContainer.classList.add("active");
        } else if (errorResponse && typeof errorResponse === "object") {
          const errorCode = errorResponse.code || "";
          let errorMessage = errorResponse.message || "An error occurred";
          
          // Fire analytics
          const formStatus = this.EXPERIAN_ERROR_CODES.includes(errorResponse.code) ? "Brochure failure - experian" : "Brochure failure - front end";
          this.fireFormStatusAnalytics(formStatus);

          // Map to a field if possible
          const fieldName = errorCodeMapping[errorCode];

          if (fieldName) {
            // Check if it's a multi-field error
            if (Array.isArray(fieldName)) {
              // For errors that affect multiple fields
              fieldName.forEach(field => {
                if (this.formFields[field]) {
                  let specificMessage = "";
                  
                  // Set appropriate message for each field
                  if (field === "email") {
                    specificMessage = this.DEFAULT_EMAIL_MESSAGE;
                  } else if (field === "mobile") {
                    specificMessage = this.DEFAULT_MOBILE_MESSAGE;
                  } else {
                    specificMessage = errorMessage;
                  }
                  
                  const arr = Array.isArray(this.formFields[field]) 
                    ? this.formFields[field] 
                    : [this.formFields[field]];
                  
                  // Find visible field to show error on
                  const visibleOrFirst = 
                    arr.find(entry => !entry.container.classList.contains("hidden")) || 
                    arr[0];
                  
                  visibleOrFirst.errorEl.textContent = specificMessage;
                  visibleOrFirst.errorEl.classList.add("active");
                  //focus on the field
                  const fieldElement = visibleOrFirst.element || document.querySelector(`input[name="${fieldName}"]`);
                  if (fieldElement) {
                    fieldElement.focus();
                    fieldElement.scrollIntoView({
                      behavior: "smooth",
                      block: "center",
                      inline: "center"
                    });
                  }
                }
              });
            } else {
              // Single field error
              // For mobile and email errors, use the default messages if needed
              if (fieldName === "mobile" && !errorMessage) {
                errorMessage = this.DEFAULT_MOBILE_MESSAGE;
              } else if (fieldName === "email" && !errorMessage) {
                errorMessage = this.DEFAULT_EMAIL_MESSAGE;
              }

              if (this.formFields[fieldName]) {
                const arr = Array.isArray(this.formFields[fieldName]) 
                  ? this.formFields[fieldName] 
                  : [this.formFields[fieldName]];
                
                // Find visible field to show error on
                const visibleOrFirst = 
                  arr.find(entry => !entry.container.classList.contains("hidden")) || 
                  arr[0];
                
                visibleOrFirst.errorEl.textContent = errorMessage;
                visibleOrFirst.errorEl.classList.add("active");

                // Focus and scroll to the field
                const fieldElement = visibleOrFirst.element || document.querySelector(`input[name="${fieldName}"]`);
                if (fieldElement) {
                  fieldElement.focus();
                  fieldElement.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                  });
                }
              }
            }
          } else {
            // Show as general error
            generalErrorContainer.textContent = errorMessage;
            generalErrorContainer.classList.add("active");
          }
        } else if (Array.isArray(errorResponse)) {
          // If multiple errors, process each one
          errorResponse.forEach((error) => {
            this.handleServerErrors(error);
          });
        }
      }

      fireFormStatusAnalytics(status) {
        if (!window.RMIT.analytics) {
          return;
        }

        window.RMIT.analytics.pushToDataLayer({
          event: "rmitoFormStatus",
          eventAction: "form_submission_status",
          formStatus: status
        });
      }

      async fireFormSubmitAnalytics(payload) {
        if (!window.RMIT.analytics) {
          return;
        }

        let additionalData = {};
        
        if (this.pageType === "course" || this.page_type === "bundle") {
          additionalData = {
            contactType: payload.isB2B ? "Business" : "Individual",
            item_name: window.analyzify.getProductObj.product.title || "",
            course_code: this.courseId || "",
          };
        }
        
        const successEvent = this.formConfig.analyticsSuccessEvent;
        const hashedData = await window.RMIT.analytics.hashUserData(payload.email, payload.mobile);

        window.RMIT.analytics.pushToDataLayer({
            event: "rmitoFormSubmit",
            leadType: successEvent && successEvent.name ? successEvent.name : "",
            user_email_hashed: hashedData.emailHashed,
            user_mobile_hashed: hashedData.mobileHashed,
            ...additionalData,
        });
      }
    }
  );
}
